import { Link } from 'react-router-dom';
import { Heart, Shield, Users, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';

export const About = () => {
  return (
    <div className="min-h-screen" data-testid="about-page">
      <SEO
        title="About Us"
        description="Learn about K&K Medical and Dental's mission to provide high-quality, affordable healthcare in Umzinto and Scottburgh, KZN."
        keywords="about K&K Medical, Umzinto clinic, Scottburgh clinic, affordable healthcare, mission statement"
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-50 to-teal-50/30 py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-6">
            Who We Are
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-6">
            About K&K Medical & Dental
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
            A healthcare practice built on the belief that every person — regardless of background or income — deserves compassionate, quality medical care.
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 lg:py-20 bg-white" data-testid="mission-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-3xl p-8 lg:p-12 text-center mb-16">
            <Target className="w-10 h-10 text-teal-200 mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">Our Mission</h2>
            <p className="text-lg lg:text-xl text-teal-50 leading-relaxed max-w-3xl mx-auto italic">
              "To provide the highest quality of healthcare which is technologically driven at the most affordable rates, for every patient, from any background, in the most compassionate, inclusive, and efficient manner."
            </p>
          </div>

          {/* Affordable ≠ Inferior */}
          <div className="max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">
              Affordable Care. No Compromises.
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed text-base sm:text-lg">
              <p>
                South Africa faces an unemployment rate above 32%, with many communities under severe economic pressure. For too many families, a visit to the doctor is a luxury they cannot afford. We refuse to accept that.
              </p>
              <p>
                At K&K Medical and Dental, low rates do not mean lower standards. Our practice invests in modern, technology-driven care — from digital diagnostics to efficient patient management — to reduce overhead without cutting corners on the quality of treatment you receive.
              </p>
              <p>
                We built this practice for the community we serve. When the economy makes life harder, healthcare should not be another burden. It should be a place of relief, dignity, and trust.
              </p>
            </div>
          </div>

          {/* Values */}
          <div className="grid sm:grid-cols-2 gap-6">
            <Card className="border-0 shadow-lg rounded-2xl" data-testid="value-compassion">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="p-3 bg-teal-100 rounded-xl shrink-0">
                  <Heart className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg mb-1">Compassionate</h3>
                  <p className="text-slate-600 text-sm">Every patient is treated with empathy, patience, and respect — no matter the visit.</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg rounded-2xl" data-testid="value-inclusive">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl shrink-0">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg mb-1">Inclusive</h3>
                  <p className="text-slate-600 text-sm">We welcome all patients from any background — medical aid or cash, every person matters equally.</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg rounded-2xl" data-testid="value-quality">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="p-3 bg-purple-100 rounded-xl shrink-0">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg mb-1">Quality-Driven</h3>
                  <p className="text-slate-600 text-sm">Modern equipment and technology-led care ensure accurate diagnoses and effective treatment.</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg rounded-2xl" data-testid="value-efficient">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="p-3 bg-amber-100 rounded-xl shrink-0">
                  <Target className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg mb-1">Efficient</h3>
                  <p className="text-slate-600 text-sm">Walk-in friendly with minimal wait times. Your time and health are equally valued.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-4">
            Your Health, Our Priority
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Whether it's a routine check-up or specialised care, we're here for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8 py-6 text-lg shadow-lg" data-testid="about-book-btn">
              <a href="/booking" onClick={() => window.scrollTo(0, 0)}>Book Appointment</a>
            </Button>
            <Button asChild variant="outline" className="rounded-full px-8 py-6 text-lg border-slate-300" data-testid="about-services-btn">
              <Link to="/services" onClick={() => window.scrollTo(0, 0)}>View Our Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
